import React, { FC, ReactElement, useEffect, useState } from "react";
import { toast } from "react-toastify";
import ActivityFrame from "../../components/activity-frame/activity-frame";
import AssignedTab from "../../components/assignWizard/AssignedTab";
import AssignModalTwo from "../../components/assignWizard/AssignModal";
import ManageButton from "../../components/assignWizard/ManageButton";
import { useDialog } from "../../components/dialog/DialogContext";
import Tabs from "../../components/tabs/tabs";
import { ACCESS_TYPES } from "../../constants/access-constants";
import { useLocale } from "../../contexts/localeContext";
import { TranslationKeys } from "../../contexts/TranslationKeys";
import AccessGroupColumns from "../../models/ColumnDefinitions/AccessGroupColumns";
import AccessProfileColumn from "../../models/ColumnDefinitions/AccessProfileColumn";
import { ListQuery } from "../../models/ListQuery";
import { Lookup } from "../../models/Lookup";
import LookupTypes from "../../models/LookupTypes";
import AccessAreaSearch from "../../models/search/AccessAreaSearch";
import AccessGroupSearch from "../../models/search/AccessGroupSearch";
import AccessProfileSearch from "../../models/search/AccessProfileSearch";
import AccessTimePatternSearch from "../../models/search/AccessTimePatternSearch";
import SiteSearch from "../../models/search/SiteSearch";
import { TableColumn } from "../../models/table/TableColumn";
import { TabSummary } from "../../models/TabSummary";
import { extendColumnDefinition, extendLookups, extendSearchColumn, hideColumns } from "../../models/utilities/columnHelper";
import { getAccessAreaNamesWithPagination } from "../../services/accessAreaService";
import { getAccessProfileAccessGroupsById } from "../../services/accessGroupProfileService";
import { getAccessGroupsForPerson, getAccessGroupsNamesWithPagination, getAllAccessGroups } from "../../services/accessGroupService";
import { getAccessProfilesForPerson, getAccessProfilesNamesWithPagination, getAllAccessProfiles } from "../../services/accessProfileService";
import { getAccessTimePatternNames } from "../../services/accessTimePatternService";
import { getAccessProfileAndGroupCounts } from "../../services/enrolmentService";
import { assignAndUnassignMasterGroupProfile } from "../../services/masterGroupProfileService";
import { assignUnassignMasterGroup } from "../../services/masterGroupService";
import { getSiteNamesWithPagination } from "../../services/siteService";
import { getErrorMessage, showError } from "../../services/utilities/ServiceError";
import { getDayNames } from "../../utilities/DateUtils";
import { appendFilter } from "../../utilities/filters";
import { PaginationModel } from "@/models/Pagination";

interface UserDetailsAccessTabTwoProps {
    masterId: string;
}

type EntityType = "ACCESS_GROUP" | "ACCESS_PROFILE" | "FLOOR_GROUP";

const UserDetailsAccessTabTwo: FC<UserDetailsAccessTabTwoProps> = ({ masterId }) => {
    const dialog = useDialog();
    const { translate } = useLocale();
    const [busy, setBusy] = useState<boolean>(false);
    const initialTabs: TabSummary[] = [
        { label: translate.byKey(TranslationKeys.accessGroups), badge: 0, showInitial: true, isActive: true },
        { label: translate.byKey(TranslationKeys.floorGroups), badge: 0, showInitial: false, isActive: true },
        { label: translate.byKey(TranslationKeys.accessProfiles), badge: 0, showInitial: true, isActive: true },
    ];
    const [tabs, setTabs] = useState<TabSummary[]>(initialTabs);
    const [selectedTab, setSelectedTab] = useState(0);
    const [showManageModal, setShowManageModal] = useState<boolean>(false);
    const [beingAssigned, setBeingAssigned] = useState<EntityType>("ACCESS_GROUP");

    useEffect(() => {
	    fetchAccessProfileAndGroupCounts(masterId);
    }, [masterId]);

    const fetchAccessProfileAndGroupCounts = async (masterId: any) => {
        try {
            const counts = await getAccessProfileAndGroupCounts(masterId);

            setTabs(currentTabs =>
                updateTabBadgeTotal(2, counts.accessProfileCount,
                    updateTabBadgeTotal(1, counts.floorGroupCount ?? 0,
                        updateTabBadgeTotal(0, counts.accessGroupCount ?? 0,currentTabs)
                    )
                )
            );

        } catch (error) {
            showError(error, toast.warn);
        }
    };

    const updateTabBadgeTotal = (tabIndex: number, totalCount: number, currentTabs: TabSummary[]) => {
        const newTabs = [...currentTabs];
        newTabs[tabIndex].badge = totalCount;
        return newTabs;
    };

    const AccessGroupLookup: Lookup[] = [
        {
            name: LookupTypes.site,
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.accessTimePattern,
            values: [],
            dataMethod: getAccessTimePatternNames,
            searchAccessor: AccessTimePatternSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.area,
            values: [],
            dataMethod: (listQuery?: ListQuery) => getAccessAreaNamesWithPagination(null, listQuery),
            searchAccessor: AccessAreaSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.days,
            values: getDayNames(translate)
        }
    ]

    const extendAssignedAccessGroupsColumns = (): TableColumn[] => {
        const columnsToExtend = AccessGroupColumns.filter(column => column.accessor !== "days");
        const extendedColumns = extendColumnDefinition(columnsToExtend, 'accessGroup');
        const daysColumn = AccessGroupColumns.find(column => column.accessor === "days");
        return daysColumn ? [...extendedColumns, daysColumn] : extendedColumns;
    };

    const tabContent: ReactElement[] = [
            <AssignedTab
                key={"AccessGroup-tab"}
                column={extendAssignedAccessGroupsColumns()}
                searchColumn={extendSearchColumn(AccessGroupSearch, "accessGroup")}
                dataMethod={(listParameters?: ListQuery) =>
                    getAccessGroupsForPerson(translate, {
                        ...listParameters,
                        filters: appendFilter(
                            masterId,
                            "Master",
                            "master.id",
                            appendFilter(
                                ACCESS_TYPES.ACCESS_GROUP,
                                "Type",
                                "accessGroup.agType",
                                listParameters?.filters ?? []
                            )
                        ),
                    })
                }
                id={masterId}
                lookup={extendLookups(AccessGroupLookup, 'accessGroup')}
                title={translate.byKey(TranslationKeys.assignedAccessGroups)}>
                <ManageButton onClick={() => setShowManageModal(true)}/>
            </AssignedTab>,
            <AssignedTab
                key={"floorGroup-tab"}
                column={extendAssignedAccessGroupsColumns()}
                searchColumn={extendSearchColumn(AccessGroupSearch, "accessGroup")}
                dataMethod={(listParameters?: ListQuery) =>
                    getAccessGroupsForPerson(translate, {
                        ...listParameters,
                        filters: appendFilter(
                            masterId,
                            "Master",
                            "master.id",
                            appendFilter(
                                ACCESS_TYPES.FLOOR_GROUP,
                                "Type",
                                "accessGroup.agType",
                                listParameters?.filters ?? []
                            )
                        ),
                    })
                }
                id={masterId}
                lookup={extendLookups(AccessGroupLookup, 'accessGroup')}
                title={translate.byKey(TranslationKeys.assignedFloorGroups)}>
                <ManageButton onClick={() => setShowManageModal(true)} />
            </AssignedTab>,
            <AssignedTab
                key={"AccessProfile-tab"}
                title={translate.byKey(TranslationKeys.assignedAccessProfiles)}
                dataMethod={(listParameters?: ListQuery) => getAccessProfilesForPerson(masterId, listParameters)}
                column={AccessProfileColumn}
                searchColumn={extendSearchColumn(AccessProfileSearch, "accessProfile")}
                id={masterId}>
                <ManageButton onClick={() => setShowManageModal(true)} />
            </AssignedTab>
    ];

    const getAssignedItems = async (setSelectedIds: React.Dispatch<React.SetStateAction<string[]>>, setSelectedRows: React.Dispatch<React.SetStateAction<any[]>>, setOriginalSelectedIds: React.Dispatch<React.SetStateAction<string[]>>, setOriginalSelectedRows: React.Dispatch<React.SetStateAction<any[]>>, setAlwaysSelectedAndDisabled?: React.Dispatch<React.SetStateAction<string[]>>) => {
        try {
            let ids: string[] = [], pageNumber = 1, pagination: PaginationModel, rows: any[] = [];
            
            if (beingAssigned === "ACCESS_GROUP" || beingAssigned === "FLOOR_GROUP") {
                const agType = beingAssigned === "ACCESS_GROUP" ? ACCESS_TYPES.ACCESS_GROUP : ACCESS_TYPES.FLOOR_GROUP;
                const filters = appendFilter(agType, "Type", "accessGroup.agType", appendFilter(masterId, "Master", "master.id", []));

                do {
                    const masterGroups = await getAccessGroupsForPerson(translate, { filters, pageSize: 100, goToPage: pageNumber });
                    ids = ids.concat(masterGroups.rows.map(masterGroup => masterGroup.id.toString()));
                    rows = rows.concat(masterGroups.rows);
                    pagination = masterGroups.pagination;
                    pageNumber++;
                } while (pagination && pageNumber <= pagination.totalPages);

                const accessProfiles = (await getAccessProfilesForPerson(masterId)).rows;
                // Find all access groups that a linked with a access profiles 
                if (accessProfiles.length > 0) {
                    const accessProfilesIds: string[] = accessProfiles.map(accessGroup => accessGroup.id.toString());
                    const masterAccessGroupIds = await getAccessProfileAccessGroupsById(accessProfilesIds.join(','));
                    setAlwaysSelectedAndDisabled && setAlwaysSelectedAndDisabled(masterAccessGroupIds);
                }
            }
            else if (beingAssigned === "ACCESS_PROFILE") {
                do {
                    const accessProfiles = await getAccessProfilesForPerson(masterId, { pageSize: 100, goToPage: pageNumber });
                    ids = ids.concat(accessProfiles.rows.map(profile => profile.id.toString()));
                    rows = rows.concat(accessProfiles.rows);
                    pagination = accessProfiles.pagination;
                    pageNumber++;
                } while (pageNumber <= pagination.totalPages);
            }

            setSelectedIds(ids);
            setSelectedRows(rows);
            setOriginalSelectedIds(ids);
            setOriginalSelectedRows(rows);
        } catch (error) {
            showError(error, toast.warn);
        }
    }

    const handleSubmit = (toBeAssigned: string[], toBeUnassigned: string[]) => {
        setBusy(true);
        let responsePromises: Promise<unknown[]> = Promise.resolve([]);
        
        //Floor group is access group we should use same method to assign and unassign
        if (beingAssigned === "ACCESS_GROUP" || beingAssigned === "FLOOR_GROUP") {
            responsePromises = assignUnassignMasterGroup(toBeAssigned, toBeUnassigned, masterId)
        }
        else if (beingAssigned === "ACCESS_PROFILE") {
            responsePromises = assignAndUnassignMasterGroupProfile(toBeAssigned, toBeUnassigned, masterId, "MASTER");
        }

        responsePromises.then(() => {
            fetchAccessProfileAndGroupCounts(masterId);
        }).catch((error) => {
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) });
        }).finally(() => {
            setBusy(false);
        });
    }

    const onTabChange = (index: number) => {
        setSelectedTab(index);
        setBeingAssigned(getBeingAssigned(index));
    }
    
    const getBeingAssigned = (index: number): EntityType => {
        if (index == 0) {
            return "ACCESS_GROUP";
        } else if (index == 1) {
            return "FLOOR_GROUP";
        }
        return "ACCESS_PROFILE";
    }

    const getAssigningTitle = (beingAssigned: EntityType) => {
        if (beingAssigned === "ACCESS_GROUP") {
            return translate.byKey(TranslationKeys.accessGroups);
        }
        else if (beingAssigned === "ACCESS_PROFILE") {
            return translate.byKey(TranslationKeys.accessProfiles);
        }
        return translate.byKey(TranslationKeys.floorGroups);
    }

    const getModalTitle = (beingAssigned: EntityType) => {
        if (beingAssigned === "ACCESS_GROUP") {
            return translate.byKey(TranslationKeys.assignAccessGroups);
        }
        else if (beingAssigned === "ACCESS_PROFILE") {
            return translate.byKey(TranslationKeys.assignAccessProfiles);
        }
        return translate.byKey(TranslationKeys.assignFloorGroups);
    }

    const getDataMethod = (beingAssigned: EntityType, listParameters?: ListQuery) => {
        if (beingAssigned === "ACCESS_GROUP") {
            return getAllAccessGroups(1, translate, listParameters)
        }
        else if (beingAssigned === "ACCESS_PROFILE") {
            return getAllAccessProfiles(listParameters)
        }
        return getAllAccessGroups(2, translate, listParameters)
    }

    return <ActivityFrame busy={busy} className='flex flex-col flex-grow bg-white' >
        <div className="w-full overflow-hidden" >
            <Tabs
                tabs={tabs}
                onSelect={onTabChange}
                selected={selectedTab}
                add={false}
                onAdd={() => { }}
                isFlat={true}
                className="w-full"
                showBorder={false}>
                <hr />
                {tabContent[selectedTab]}
            </Tabs>
        </div>
        <AssignModalTwo
            assigningTitle={getAssigningTitle(beingAssigned)}
            dataMethod={(listParameters?: ListQuery) => getDataMethod(beingAssigned, listParameters)}
            getAssignedItems={getAssignedItems}
            isOpen={showManageModal}
            modalTitle={getModalTitle(beingAssigned)}
            onClose={() => setShowManageModal(false)}
            searchColumns={beingAssigned === "ACCESS_PROFILE" ?  AccessProfileSearch : AccessGroupSearch}
            tableColumns={beingAssigned === "ACCESS_PROFILE" ? AccessProfileColumn : hideColumns(AccessGroupColumns, ['accessTimePatterns', "days", "startTime", "duration", "enrolments"])}
            lookup={beingAssigned === "ACCESS_PROFILE" ? [] : AccessGroupLookup} 
            handleSubmit={handleSubmit}/>
    </ActivityFrame>
}

export default UserDetailsAccessTabTwo;
